import {DirectiveBinding} from "vue/types/options";
import {VNode} from "vue";
import {fail} from "./util";
import {readData} from "./data";
import {findDirectChildElementOf, getDirectChildIndex} from "germanium-dom-util";


/**
 * Keeps all our registered event listeners so we don't pass
 * 1000 params around.
 */
class DropContainer {
    private readonly dropCallback: Function;
    private readonly vNode: VNode;
    private readonly css: boolean;
    private readonly isList: boolean;
    private readonly isHorizontal: boolean;

    private hoverCount: number = 0;
    private hoverChild!: Element | null;
    private hoverChildIndex: number = -1; // the actual index of the child element

    constructor(vNode: VNode,
                directiveBinding: DirectiveBinding) {
        if (!directiveBinding.value) {
            fail("No drop callback configured for a drop zone")
        }

        this.dropCallback = directiveBinding.value
        this.vNode = vNode
        this.css = directiveBinding.modifiers.css
        this.isList = directiveBinding.modifiers.list || directiveBinding.modifiers.hlist
        this.isHorizontal = directiveBinding.modifiers.hlist
    }

    /**
     * Called repeatedly when dragging over. If the dnd_dragover is defined
     * the result is interpreted. If CSS is defined the `dnd-drop-rejected`
     * is updated accordingly.
     */
    dragOver = (ev: DragEvent) => {
        const computedIndex = this.computeIndex(ev);

        this.clearChildIndexClasses()

        if (!this.vNode.data?.on?.dnd_dragover) {
            permitDrop(ev)
            this.removeCssClasses('dnd-drop-rejected')
            this.setChildIndexClasses(computedIndex)

            return
        }

        const callback = <Function>this.vNode.data?.on?.dnd_dragover

        const result = callback(
            readData(ev.dataTransfer),
            ev,
            computedIndex,
        )

        // if the callback is returning a value, and is `false` we don't allow the drop
        if (typeof result != "undefined" && !result) {
            this.addCssClasses('dnd-drop-rejected')
            return
        }

        this.setChildIndexClasses(computedIndex)

        this.removeCssClasses('dnd-drop-rejected')
        permitDrop(ev)
    }

    /**
     * Calls the defined drop action.
     */
    drop = (ev: DragEvent) => {
        permitDrop(ev)

        this.dropCallback(
            readData(ev.dataTransfer),
            ev,
            this.computeIndex(ev),
        )

        this.hoverCount = 0  // we reset the hover count
        this.removeCssClasses("dnd-drop-hover", "dnd-drop-rejected")
        this.clearChildIndexClasses()
    }

    /**
     * Marks the class as being hovered, and notifies the enter.
     */
    dragEnter = (ev: DragEvent) => {
        this.hoverCount += 1

        let computedIndex = -1

        // FIXME: probably another event can exist
        // We run the update when entering each child, but we don't trigger the enter notification
        // since dragOver should be enough then. If later we find out is important, we can implement
        // another event: @dnd_dragenterchild, so we can track index changes.
        if (this.isList) {
            this.clearChildIndexClasses();

            this.hoverChild = findDirectChildElementOf(this.vNode.elm, <Node>ev.target)
            this.hoverChildIndex = this.hoverChild ? getDirectChildIndex(this.hoverChild) : -1
            computedIndex = this.computeIndex(ev)

            this.setChildIndexClasses(computedIndex);
        }

        // FIXME: if I drop a file the count somehow remains +1 forever
        //        but the style is somehow lost.
        if (this.hoverCount >= 2) {
            return
        }

        this.addCssClasses("dnd-drop-hover")

        const callback = <Function>this.vNode.data?.on?.dnd_dragenter

        // we need to call the callback only if we have a callback
        if (callback) {
            callback(
                readData(ev.dataTransfer),
                ev,
                computedIndex,
            )
        }

        // We need to run the drag over as well to see if there's a `dnd_dragover`
        // that might style this as not acceptable. Otherwise for a short time
        // the container will flicker as just hovered without the rejected class.
        if (this.css) {
            this.dragOver(ev)
        }

    }

    /**
     * Clears the hover/rejected styling, and calls the callback if defined.
     */
    dragLeave = (ev: DragEvent) => {
        this.hoverCount -= 1

        // FIXME: this fixes the issue with the file drop nuking all hovering
        //        but it introduces the bug in dragEnter.
        if (this.hoverCount < 0) {
            this.hoverCount = 0;
        }

        if (this.hoverCount > 0) {
            return
        }

        this.removeCssClasses("dnd-drop-hover", "dnd-drop-rejected")
        this.clearChildIndexClasses()

        const callback = <Function>this.vNode.data?.on?.dnd_dragleave

        if (!callback) {
            return;
        }

        callback(
            readData(ev.dataTransfer),
            ev,
            -1, // this is -1 since it leaves the component anyway
        )
    }

    private addCssClasses(...cssClasses: string[]) {
        if (!this.css) {
            return
        }

        const element = <Element>this.vNode.elm
        element.classList.add(...cssClasses)
    }

    private removeCssClasses(...cssClasses: string[]) {
        if (!this.css) {
            return
        }

        const element = <Element>this.vNode.elm
        element.classList.remove(...cssClasses)
    }

    /**
     * Compute the index where the drop should be performed. This looks
     * if the items are in a horizontal list (with the horizontal modifier)
     * and looks at the offset relative to the center of the element.
     *
     * If it's passed the center, it adds one to the index.
     */
    private computeIndex(ev: DragEvent): number {
        if (!this.isList) {
            return -1
        }

        if (!this.hoverChild) {
            return -1;
        }

        if (this.isHorizontal) {
            if (ev.offsetX > this.hoverChild.clientWidth / 2) {
                return this.hoverChildIndex + 1
            }
        } else {
            if (ev.offsetY > this.hoverChild.clientHeight / 2) {
                return this.hoverChildIndex + 1
            }
        }

        return this.hoverChildIndex;
    }

    private setChildIndexClasses(computedIndex: number) {
        if (this.css && this.hoverChild) {
            if (this.isHorizontal) {
                this.hoverChild.classList.add(
                    this.hoverChildIndex == computedIndex ?
                        'dnd-drop-index-h-before' :
                        'dnd-drop-index-h-after'
                )
            } else {
                this.hoverChild.classList.add(
                    this.hoverChildIndex == computedIndex ?
                        'dnd-drop-index-before' :
                        'dnd-drop-index-after'
                )
            }
        }
    }

    private clearChildIndexClasses() {
        if (this.css && this.hoverChild) {
            if (this.isHorizontal) {
                this.hoverChild.classList.remove(
                    'dnd-drop-index-h-before',
                    'dnd-drop-index-h-after',
                )
            } else {
                this.hoverChild.classList.remove(
                    'dnd-drop-index-before',
                    'dnd-drop-index-after',
                )
            }
        }
    }
}

export function registerDropContainer(element: HTMLElement,
                                      binding: DirectiveBinding,
                                      vnode: VNode,
                                      oldVnode: VNode) {
    const dropContainerEvents = new DropContainer(vnode, binding)

    if (vnode.data?.on?.dnd_dragenter || binding.modifiers.css) {
        element.addEventListener("dragenter", dropContainerEvents.dragEnter)
    }

    if (vnode.data?.on?.dnd_dragleave || binding.modifiers.css) {
        element.addEventListener("dragleave", dropContainerEvents.dragLeave)
    }

    element.addEventListener("dragover", dropContainerEvents.dragOver)
    element.addEventListener("drop", dropContainerEvents.drop)
}

/**
 * Unlike normal APIs, the drag and drop browser API uses the prevent
 * default to deal with "accepting" the drop.
 */
function permitDrop(ev: DragEvent) {
    ev.preventDefault()
    ev.stopImmediatePropagation()
}
